import {Injectable, OnDestroy} from '@angular/core';
import {DateTime} from 'luxon';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MessageBusService} from '../message-bus.service';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {UserSettingsService} from '../user-settings.service';
import {isValidNumber} from '../utils';
import {
    HorizontalSkewBasis,
    HorizontalSkewChangedDto,
    HorizontalSkewPrefs,
    HorizontalSkewPrefsStore,
    SubscribeHorizontalSkew,
    UnsubscribeHorizontalSkew
} from './horizontal-skew-cqrs.model';

// Temporary end-to-end diagnostic switch (CR-20260701-001). Flip to false to silence.
const HS_TRACE = true;

// What this surface actually holds on the backend. The backend ref-counts holders by
// exact (auth, underlying, basis, shortDays, longDays) — commands must pair up EXACTLY,
// or an extra Unsubscribe removes a SIBLING surface's identical registration (two
// surfaces on the same legs are indistinguishable server-side) and silently freezes it.
interface HsSubscribedPair {
    underlying: string;
    basis: HorizontalSkewBasis;
    short: number;
    long: number;
}

/**
 * One Horizontal-Skew reading for one surface (the SPX tray, or one OPG panel).
 * Instance-scoped — the tray module and each OPG panel provide their own instance,
 * each with its own legs / basis / underlying / prefs store.
 *
 * Legs are DAY COUNTS and go on the wire as day counts (ruling D19): the BACKEND
 * resolves them to listed expirations against the as-of date of the world it quotes
 * in (the QG cursor date in replay, today in live) and re-resolves on cursor/mode
 * changes. This service subscribes to the per-(underlying, basis) live push,
 * self-filters it, reads its own pair's resolved legs from the push, and divides
 * those legs out of the pushed expiration set to form the ratio. Legs and basis are
 * remembered through the surface's prefs store — per user for the tray, PER SYMBOL
 * for OPG panels (first use of a symbol starts at the 30/90-day defaults).
 */
@Injectable()
export class HorizontalSkewService implements OnDestroy {

    // ── Reading state ──
    ratio: number | null = null;
    shortIv: number | null = null;
    longIv: number | null = null;
    loading = true;

    // ── Per-surface settings (day counts — the only leg state this side owns) ──
    short = 30;
    long = 90;
    basis: HorizontalSkewBasis = 'spot';

    // ── Notification (component subscribes for change detection) ──
    updated$ = new Subject<void>();

    private _underlying: string | null = null;
    private _prefsStore: HorizontalSkewPrefsStore | null = null;
    private _shortExp: string | null = null;      // backend-resolved legs, read from the push
    private _longExp: string | null = null;
    private _shortDte: number | null = null;
    private _longDte: number | null = null;
    private _subscribedPair: HsSubscribedPair | null = null;
    private _lastDto: HorizontalSkewChangedDto | null = null;
    // Recreated by init(): a takeUntil bound to the completed subject of a previous
    // dispose() could never tear the new subscription down (see init()).
    private _unsubscriber = new Subject<void>();

    constructor(
        private readonly _shellClient: ShellClientService,
        private readonly _messageBus: MessageBusService,
        private readonly _userSettings: UserSettingsService,
    ) {
    }

    async init(underlying: string, prefs_: string | HorizontalSkewPrefsStore): Promise<void> {
        this._underlying = underlying;
        // A string is a per-user UserSettingsService key (the tray; underlying ignored);
        // an OPG panel passes its own store keyed PER SYMBOL (`${underlying}.opg.hskew`).
        if (typeof prefs_ === 'string') {
            const key = prefs_;
            this._prefsStore = {
                load: () => this._userSettings.getValue<HorizontalSkewPrefs>(key),
                save: (_ul, p) => this._userSettings.setValue(key, p)
            };
        } else {
            this._prefsStore = prefs_ || null;
        }

        this.loadPrefs();

        // The module-provided tray instance SURVIVES a logout navigation while its
        // component is destroyed (dispose() completed the old notifier). Rewire on a
        // FRESH subject: takeUntil bound to an already-completed subject can never
        // fire again, so the new subscription would be immortal — one leaked
        // recompute-per-push subscriber for every logout/login cycle.
        this._unsubscriber.next();
        this._unsubscriber = new Subject<void>();

        this._messageBus.of<HorizontalSkewChangedDto>('HorizontalSkewChangedDto')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(msg => this.onPush(msg.payload));

        this.subscribeCurrent();
    }

    /**
     * User entered new leg day counts. `Short < Long` required. Returns true when the
     * change was APPLIED — a false lets the caller write the previous value back into
     * the widget (DX commits the entry internally before onValueChanged, and an
     * unchanged [value] binding never corrects it).
     */
    async setLegs(short: number, long: number): Promise<boolean> {
        if (!isValidNumber(short, true) || !isValidNumber(long, true)) { return false; }
        // The wire carries ints (backend ShortDays/LongDays) — a fractional value would fail
        // command deserialization server-side and freeze the surface. Normalize, don't reject.
        short = Math.round(short);
        long = Math.round(long);
        if (short <= 0 || short >= long) { return false; }
        if (short === this.short && long === this.long) { return true; }

        this.short = short;
        this.long = long;
        this.savePrefs();
        this.subscribeCurrent();
        return true;
    }

    /**
     * Switch the surface to another underlying (an OPG panel changing its symbol).
     * Settings are PER SYMBOL: the new symbol's stored prefs load (or the 30/90
     * defaults on first use) — legs do NOT carry over. The old registration is
     * released by resubscribe() from the recorded `_subscribedPair` (which carries
     * its own underlying). NOT for re-init after dispose().
     */
    async setUnderlying(underlying: string): Promise<void> {
        if (!underlying || underlying === this._underlying) { return; }

        this._underlying = underlying;
        this.loadPrefs();
        this._lastDto = null; // previous pushes belong to the old underlying — drop them
        this.subscribeCurrent();
    }

    async setBasis(basis: HorizontalSkewBasis): Promise<void> {
        if (basis !== 'spot' && basis !== 'forward') { return; }
        if (basis === this.basis) { return; }

        this.basis = basis;
        this._lastDto = null; // the previous push was on the old basis — drop it
        this.savePrefs();
        this.subscribeCurrent();
    }

    get band(): 'contango' | 'transitioning' | 'backwardation' | null {
        if (this.ratio == null) { return null; }
        if (Math.abs(this.ratio - 1) < 1e-9) { return 'transitioning'; }
        return this.ratio < 1 ? 'contango' : 'backwardation';
    }

    /** The underlying this surface reads (e.g. 'SPX'). Consumed by the chart popup. */
    get underlying(): string | null {
        return this._underlying;
    }

    // The listed expiration each day-count leg actually resolved to, for display
    // ("Wed, 03-Aug-26 (31d)"). Since D19 both the expiration AND its day count come
    // from the backend push — resolved against the as-of date of the world being
    // quoted (in QG replay the (Nd) is days from the CURSOR, not from real today).
    // Null while unresolved (no push yet / unresolvable as-of the date).
    get shortLegLabel(): string | null {
        return this.legLabel(this._shortExp, this._shortDte);
    }

    get longLegLabel(): string | null {
        return this.legLabel(this._longExp, this._longDte);
    }

    private legLabel(exp: string | null, dte: number | null): string | null {
        if (!exp) { return null; }
        const dt = DateTime.fromISO(exp);
        // Platform date format (dx-date-box displayFormat precedent): "Wed, 03-Aug-26".
        const datePart = dt.isValid ? dt.toFormat('EEE, dd-MMM-yy') : exp;
        return dte != null ? datePart + ' (' + dte + 'd)' : datePart;
    }

    dispose(): void {
        // Idempotent by construction: the backend release comes from _subscribedPair,
        // which releaseSubscription() consumes. Angular destroys a component-provided
        // service (ngOnDestroy → dispose) IN ADDITION to the component's own explicit
        // dispose() — a second unsubscribe command would remove a SIBLING surface's
        // identical (auth, legs) registration and silently freeze it.
        this.releaseSubscription();
        this._unsubscriber.next();
        this._unsubscriber.complete();
        this.ratio = null;
        this.loading = true;
        this.updated$.next();
    }

    ngOnDestroy(): void {
        this.dispose();
    }

    /**
     * (Re)register the current (underlying, basis, short, long) on the backend and show
     * progress until its push arrives. Synchronous — there is nothing to await since the
     * backend owns resolution (D19); the reading settles when the push lands (≤ one
     * 250 ms recompute cycle — Subscribe forces a re-push of current state).
     */
    private subscribeCurrent(): void {
        if (!this._underlying) { return; }

        // Progress feedback from the moment a change starts (the overlay/box templates
        // render a spinner while loading). The push settles it: number, or terminal N/A
        // (backend-unresolvable legs / no-data straddle), or spinner while converging.
        this.loading = true;
        this._shortExp = null;
        this._longExp = null;
        this._shortDte = null;
        this._longDte = null;
        this.updated$.next();

        this.resubscribe();
        this.recompute();
    }

    /**
     * Release exactly what is held (per the recorded pair — its own underlying/basis,
     * so symbol switches release correctly), then register the current legs and record
     * them. This exact bookkeeping is what keeps unsubscribe commands paired 1:1 with
     * subscribe commands under every interleaving.
     */
    private resubscribe(): void {
        this.releaseSubscription();

        this._shellClient.processCommand(
            new SubscribeHorizontalSkew(this._underlying, this.basis, this.short, this.long));
        this._subscribedPair = {
            underlying: this._underlying,
            basis: this.basis,
            short: this.short,
            long: this.long
        };

        if (HS_TRACE) {
            console.log('[HS] subscribe underlying=%s basis=%s legs=%s/%sd',
                this._underlying, this.basis, this.short, this.long);
        }
    }

    private releaseSubscription(): void {
        const held = this._subscribedPair;
        if (!held) { return; }
        this._subscribedPair = null;
        try {
            this._shellClient.processCommand(
                new UnsubscribeHorizontalSkew(held.underlying, held.basis, held.short, held.long));
        } catch {
            // session may already be gone at logout — backend CleanupSession covers it.
        }
    }

    private loadPrefs(): void {
        this.short = 30;
        this.long = 90;
        this.basis = 'spot';

        const prefs = this._prefsStore && this._underlying ? this._prefsStore.load(this._underlying) : null;
        if (!prefs) { return; }

        // Round on load too — previously-persisted fractional day counts must not reach the
        // int wire (see setLegs).
        if (isValidNumber(prefs.short, true)) { this.short = Math.round(prefs.short); }
        if (isValidNumber(prefs.long, true)) { this.long = Math.round(prefs.long); }
        if (prefs.basis === 'spot' || prefs.basis === 'forward') { this.basis = prefs.basis; }
    }

    private onPush(dto: HorizontalSkewChangedDto): void {
        // Contain any throw here: this runs inside a message-bus subscriber, and an
        // uncaught throw makes RxJS permanently unsubscribe this observer — one bad
        // message would silently freeze every future live update (the exact spinner
        // bug this CR hit). Keep the pipe alive across malformed messages.
        try {
            if (HS_TRACE) {
                const accepted = !!dto && dto.underlying === this._underlying && dto.basis === this.basis;
                console.log('[HS] recv underlying=%s basis=%s asOf=%s legs=%o exps=%o | mine underlying=%s basis=%s | accepted=%s',
                    dto && dto.underlying, dto && dto.basis, dto && dto.asOfDate, dto && dto.legs, dto && dto.expirations,
                    this._underlying, this.basis, accepted);
            }
            if (!dto || dto.underlying !== this._underlying || dto.basis !== this.basis) {
                return; // self-filter: only this surface's (underlying, basis)
            }
            this._lastDto = dto;
            this.recompute();
        } catch (e) {
            console.error('[HS] onPush failed (subscription preserved)', e);
        }
    }

    private recompute(): void {
        const dto = this._lastDto;
        if (!dto) {
            this.setWaiting();
            return;
        }

        // Our pair's backend resolution. `legs` can arrive null (protobuf-net collapses
        // an empty repeated field to null across the ESS round-trip — same trap as
        // `expirations`); a push predating our Subscribe may also not carry our pair yet.
        const legs = (dto.legs || []).find(l => l.shortDays === this.short && l.longDays === this.long);
        if (!legs) {
            this.setWaiting();
            return;
        }

        this._shortExp = legs.shortExpiration || null;
        this._longExp = legs.longExpiration || null;
        this._shortDte = legs.shortDte != null ? legs.shortDte : null;
        this._longDte = legs.longDte != null ? legs.longDte : null;

        if (dto.loading) {
            // Slot still assembling (chain fetch in flight) — resolutions are not final.
            this.setWaiting();
            return;
        }

        // Settled unresolvable: the backend found no listed candidate as-of the world's
        // date (no non-expired options / nothing quotable at the cursor) — N/A, not a
        // spinner. A cursor jump / leg / symbol / basis change re-resolves.
        if (!this._shortExp || !this._longExp) {
            this.ratio = null;
            this.shortIv = null;
            this.longIv = null;
            this.loading = false;
            this.updated$.next();
            return;
        }

        const exps = dto.expirations || [];
        const s = exps.find(e => e.expiration === this._shortExp);
        const l = exps.find(e => e.expiration === this._longExp);
        this.shortIv = s && s.iv != null ? s.iv : null;
        this.longIv = l && l.iv != null ? l.iv : null;

        if (this.shortIv != null && this.longIv != null && this.longIv !== 0) {
            this.ratio = this.shortIv / this.longIv; // UNROUNDED — the band is decided on this
            this.loading = false;
        } else {
            // A leg without IV spins while CONVERGING (loading=true on its entry) but
            // settles to N/A once the backend ANSWERS no-data (loading=false + iv null —
            // e.g. a straddle whose strike has no history rows at the QG cursor, D19).
            this.ratio = null;
            const shortSettled = !!s && !s.loading;
            const longSettled = !!l && !l.loading;
            this.loading = !(shortSettled && longSettled);
        }

        if (HS_TRACE) {
            console.log('[HS] divide short=%s(found=%s iv=%s) long=%s(found=%s iv=%s) => ratio=%s loading=%s',
                this._shortExp, !!s, this.shortIv, this._longExp, !!l, this.longIv, this.ratio, this.loading);
        }

        this.updated$.next();
    }

    private setWaiting(): void {
        this.ratio = null;
        this.shortIv = null;
        this.longIv = null;
        this.loading = true;
        this.updated$.next();
    }

    private savePrefs(): void {
        if (!this._prefsStore || !this._underlying) { return; }
        const prefs: HorizontalSkewPrefs = {short: this.short, long: this.long, basis: this.basis};
        this._prefsStore.save(this._underlying, prefs);
    }
}
